<template>
    <loading v-if="fetchingJob"/>
    <div v-else>
        <div class="flex justify-center mb-32 relative">
            <steps v-model="activeTab" :steps="tabs" container />

            <!-- Back button -->
            <div
                class="absolute back trans-ease bg-white shadow-sm py-8 px-16 rounded text-body-sm flex items-center cursor-pointer"
                @click="close"
            >
                <svg-icon type="keyboard-arrow-left" class="max-h-16 max-w-16 mr-4"/>
                {{ $t('jobs.back_to.overview') }}
            </div>
        </div>

        <keep-alive>
            <component
                :is="tabs[activeTab - 1] ? tabs[activeTab - 1].component : null"
                :job="job"
                @save="saveJob"
                @created="handleCreated"
                @close="close"
                @next="next"
                ref="jobTabs"
            ></component>
        </keep-alive>
    </div>
</template>

<script>
import PopupModal from './PopupModal.vue';
import SvgIcon from './SvgIcon.vue';
import JobSettings from './JobSettings.vue';
import PipelineEditor from './PipelineEditor.vue';
import FormEditor from './FormEditor.vue';
import PublishJob from './PublishJob.vue';
import { normalizeJson } from '../../../shared/helpers';
import Job from '../../models/Job';
import JobFilter from '../../models/JobFilter';
import * as jobs from '../../services/JobService';
import * as companies from '../../services/CompanyService';
import * as attachmentService from '../../services/AttachmentService';
import Domain from '../../models/Domain';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import PopupModalConfirm from './PopupModalConfirm.vue';
import FinalJobTab from "./FinalJobTab";
import Loading from "./Loading";
import Steps from "../Steps";
import {mapStores} from "pinia";
import {useJobsStore} from "../../state/modules/jobs";
import {useAuthenticationStore} from "../../state/modules/authentication";
import {useNotificationsStore} from "../../state/modules/notifications";
import {useRepo} from "pinia-orm";
import {useMainStore} from "../../state/store";

export default {
    components: {
        Steps,
        Loading,
        'popup-modal': PopupModal,
        'svg-icon': SvgIcon,
        'job-settings': JobSettings,
        'pipeline-editor': PipelineEditor,
        'form-editor': FormEditor,
        'publish-job': PublishJob,
        'popup-modal-confirm': PopupModalConfirm,
        'final': FinalJobTab
    },
    data: function () {
        return {
            justCreatedID: null,
            activeTab: null,
            fetchingJob: false
        }
    },
    computed: {
        ...mapStores(useMainStore, useJobsStore, useAuthenticationStore, useNotificationsStore),
        addingJob () {
            return this.jobsModuleStore.addingJob;
        },
        currentCompanyId () {
            return this.authenticationStore.currentCompanyId;
        },
        title () {
            return this.$t(this.job
                ? 'general.edit_subject'
                : 'general.add_subject',
                { subject: this.$t('jobs.position') }
            );
        },
        job () {
            return useRepo(Job).with('attachments').with('filters').find(this.$route.params.id || this.justCreatedID);
        },
        tabs() {
            return [
                {
                    title: this.$t('jobs.description'),
                    icon: 'description',
                    component: 'job-settings'
                },
                {
                    title: this.$t('jobs.form'),
                    icon: 'list-alt',
                    component: 'form-editor',
                    disabled: !this.job
                },
                {
                    title: this.$t('jobs.pipeline'),
                    icon: 'conversion-path',
                    component: 'pipeline-editor',
                    disabled: !this.job
                },
                {
                    title: this.$t('jobs.publish'),
                    icon: 'publish',
                    component: 'publish-job',
                    disabled: !this.job
                },
                {
                    title: this.$t('applications.share'),
                    icon: 'share',
                    component: 'final',
                    hidden: !this.job
                }
            ]
        }
    },
    methods: {
        close () {
            this.$router.push({ name: this.$route.query.from || 'job', params: { id: this.job?.id } });
        },
        cancelClose () {
            this.closing = false;
        },
        next () {
            this.activeTab++;
            window.bus.dispatch('popup-top');
        },
        notify () {
            this.notificationsModuleStore[ADD_NOTIFICATION]({
                type: 'success',
                message: this.$t('general.changes_saved'),
            });

            this.close(false);
        },
        setActiveTab (tab) {
            this.activeTab = tab;
        },
        handleCreated (job, todo) {
            this.justCreatedID = job.id;
            this.addJobPermissions(job);
        },
        addJobPermissions (job) {
            let permissions = this.authenticationStore.permissions;

            if (!permissions[this.mainStore.currentCompany.id])
                permissions[this.mainStore.currentCompany.id] = [];

            if (!permissions[this.mainStore.currentCompany.id].includes('jobs.view.' + job.id)) {
               permissions[this.mainStore.currentCompany.id].push('jobs.view.' + job.id);
               permissions[this.mainStore.currentCompany.id].push('jobs.manage.' + job.id);

               permissions[this.mainStore.currentCompany.id].push('job-applications.view.' + job.id);
               permissions[this.mainStore.currentCompany.id].push('job-applications.manage.' + job.id);
            }

            this.authenticationStore.setPermissions(permissions);
        },
        async saveJob ({todo, form, relationships}) {
            if (typeof relationships == 'undefined') {
                relationships = {};
            }

            if (this.job) {
                await this.updateJob(form, relationships);
            } else {
                await this.createJob(form, relationships, todo);
            }

            // Scroll to top
            document.getElementById('content-container').scrollTop = 0;
            this[todo](false);

            if (todo === 'close') {
                this.$emit('success');
            }

        },
        createJob (form, relationships, todo) {
            return form.submit(data => jobs.store({
                data: { attributes: {
                    ...data,
                    sequence: 0
                }, relationships: { ...relationships }},
            }))
            .then(result => {
                const jobs = useRepo(Job).where('company_id', this.currentCompanyId).get();
                jobs.forEach((j) => {
                    useRepo(Job).whereId(j.id).update({sequence: j.sequence + 1});
                })
                return result;
            })
            .then(result => {
                this.addAttachments(result.id, form.data.queued_attachments);
                return result.id;
            })
            .then(id => jobs.find(id))
            .then(result => useRepo(Job).save(normalizeJson(result)))
            .then(job => this.handleCreated(job))
            .then(() => {
                if (form.save_as_default) {
                    this.saveDefaultApplicationForm(form)
                }
            });
        },
        updateJob (form, relationships) {
            return form.submit(data => {
                if (this.activeTab === 1 && !data.company_location_id)
                    data.company_location_id = null;

                if (this.activeTab === 1 && !data.contract_type)
                    data.contract_type = null;

                return jobs.update(this.job.id, {
                    data: { attributes: data, relationships: { ...relationships } },
                });
            })
            .then(result => {
                this.addAttachments(result.id, form.data.queued_attachments);
                return result.id;
            })
            .then(id => jobs.find(id))
            .then(result => {
                // first drop relations
                useRepo(JobFilter).where('job_id', result.id).delete();

                // update
                return useRepo(Job).save(normalizeJson(result));
            })
            .then(() => {
                if (form.save_as_default) {
                    this.saveDefaultApplicationForm(form)
                }
            });
        },
        addAttachments (id, attachments) {
            for (let lang in attachments) {
                for (let file of attachments[lang]) {
                    let name = file.attributes.name_original.replace(/\.[a-zA-Z]+$/, '').replace(/_/g, ' ');

                    attachmentService.store(id, {
                        data: {
                            attributes: {
                                filename_queued_id: file.id,
                                title: {
                                    [lang]: name.charAt(0).toUpperCase() + name.slice(1)
                                },
                                active: { [lang]: true },
                            }
                        }
                    });
                }
            }
        },
        saveDefaultApplicationForm(form) {
            companies.updateApplicationForm(this.currentCompanyId, form.dynamic_form)
        }
    },
    mounted () {
        if (this.$route.query.step) {
            this.activeTab = parseInt(this.$route.query.step);
        } else {
            this.activeTab = 1;
        }
        this.jobsModuleStore.SET_FETCHING_AVAILABLE_PARAMETERS(true);

        jobs.parameters().then(result => {
            useRepo(Domain).save(normalizeJson(result.data.domain));
            this.jobsModuleStore.setAvailableParameters(result.data);
            this.jobsModuleStore.SET_FETCHING_AVAILABLE_PARAMETERS(false);
        });

        if (this.$route.params.id) {
            this.fetchingJob = true;

            jobs.find(this.$route.params.id).then(async result => {
                useRepo(JobFilter).flush();
                return useRepo(Job).save(normalizeJson(result));
            }).then((result) => {
                this.fetchingJob = false;
                this.$forceUpdate();
            })
        }
    }
}
</script>

<style scoped>
.back {
    left: 10px;
    top: 10px;
}

.back:hover {
    left: 0;
}
</style>
